import React, { useState } from 'react';
import { Check, X, Loader } from 'lucide-react';
import { apiRequest } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './CouponField.css';

export default function CouponField({ onCouponApplied, onCouponRemoved, selectedPlan }) {
  const { currentUser } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValid, setIsValid] = useState(null);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Ingresa un código de cupón');
      return;
    }

    setIsValidating(true);
    setIsValid(null);

    try {
      const response = await apiRequest('/api/coupons/validate', {
        method: 'POST',
        body: {
          code: couponCode.trim(),
          plan: selectedPlan
        }
      });

      if (response.success) {
        setIsValid(true);
        toast.success('¡Cupón válido! Aplicando descuento...');
        // Aplicar inmediatamente tras validar (sin requerir segundo clic)
        await applyCoupon(true);
      }
    } catch (error) {
      setIsValid(false);
      const errorMessage = error.message || 'Cupón no válido';
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const applyCoupon = async (forceApply = false) => {
    if (!couponCode.trim() || (!isValid && !forceApply)) {
      return;
    }

    if (!currentUser) {
      toast.error('Debes estar autenticado para aplicar cupones');
      return;
    }

    try {
      console.log('🔍 Aplicando cupón:', {
        code: couponCode.trim(),
        plan: selectedPlan,
        userId: currentUser.uid
      });

      // Renovar token antes de hacer la petición
      console.log('🔄 Renovando token de autenticación...');
      const freshToken = await currentUser.getIdToken(true);
      localStorage.setItem('authToken', freshToken);
      console.log('✅ Token renovado');

      // Intentar primero con el endpoint normal
      let response;
      try {
        response = await apiRequest('/api/coupons/apply', {
          method: 'POST',
          body: {
            code: couponCode.trim(),
            plan: selectedPlan,
            userId: currentUser.uid
          }
        });
      } catch (applyError) {
        console.log('🔄 Endpoint normal falló, intentando con endpoint temporal...');
        // Si falla, intentar con endpoint temporal
        response = await apiRequest('/api/coupons/temp-apply', {
          method: 'POST',
          body: {
            code: couponCode.trim(),
            plan: selectedPlan,
            userId: currentUser.uid
          }
        });
      }

      console.log('✅ Respuesta del servidor:', response);

      if (response.success) {
        setAppliedCoupon({
          code: response.coupon.code,
          discountType: response.coupon.discountType,
          discountValue: response.coupon.discountValue,
          originalPrice: response.originalPrice,
          finalPrice: response.finalPrice,
          discountAmount: response.discountAmount,
          sessionId: response.sessionId
        });

        onCouponApplied(response);
        toast.success(`¡Descuento aplicado! Ahorras $${response.discountAmount.toLocaleString()}`);
      }
    } catch (error) {
      console.error('❌ Error aplicando cupón:', error);
      console.error('❌ Error completo:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      let errorMessage = 'Error al aplicar cupón';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }
      
      // Si es error de autenticación, intentar renovar token y reintentar
      if (error.message && (error.message.includes('Token') || error.message.includes('autenticación') || error.message.includes('401'))) {
        console.log('🔄 Error de autenticación, intentando renovar token...');
        try {
          const freshToken = await currentUser.getIdToken(true);
          localStorage.setItem('authToken', freshToken);
          
          // Reintentar la petición con endpoint temporal
          const retryResponse = await apiRequest('/api/coupons/temp-apply', {
            method: 'POST',
            body: {
              code: couponCode.trim(),
              plan: selectedPlan,
              userId: currentUser.uid
            }
          });

          if (retryResponse.success) {
            setAppliedCoupon({
              code: retryResponse.coupon.code,
              discountType: retryResponse.coupon.discountType,
              discountValue: retryResponse.coupon.discountValue,
              originalPrice: retryResponse.originalPrice,
              finalPrice: retryResponse.finalPrice,
              discountAmount: retryResponse.discountAmount,
              sessionId: retryResponse.sessionId
            });

            onCouponApplied(retryResponse);
            toast.success(`¡Descuento aplicado! Ahorras $${retryResponse.discountAmount.toLocaleString()}`);
            return;
          }
        } catch (retryError) {
          console.error('❌ Error en reintento:', retryError);
          errorMessage = 'Error de autenticación. Por favor, inicia sesión nuevamente.';
        }
      }
      
      // Si es error de servidor (500), intentar con endpoint temporal
      if (error.message && (error.message.includes('500') || error.message.includes('Error interno'))) {
        console.log('🔄 Error del servidor, intentando con endpoint temporal...');
        try {
          const tempResponse = await apiRequest('/api/coupons/temp-apply', {
            method: 'POST',
            body: {
              code: couponCode.trim(),
              plan: selectedPlan,
              userId: currentUser.uid
            }
          });

          if (tempResponse.success) {
            setAppliedCoupon({
              code: tempResponse.coupon.code,
              discountType: tempResponse.coupon.discountType,
              discountValue: tempResponse.coupon.discountValue,
              originalPrice: tempResponse.originalPrice,
              finalPrice: tempResponse.finalPrice,
              discountAmount: tempResponse.discountAmount,
              sessionId: tempResponse.sessionId
            });

            onCouponApplied(tempResponse);
            toast.success(`¡Descuento aplicado! Ahorras $${tempResponse.discountAmount.toLocaleString()}`);
            return;
          }
        } catch (tempError) {
          console.error('❌ Error en endpoint temporal:', tempError);
          errorMessage = 'Error del servidor. Intenta nuevamente en unos momentos.';
        }
      }
      
      // Mostrar error más detallado en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Información adicional del error:', {
          errorMessage,
          originalError: error,
          currentUser: currentUser ? {
            uid: currentUser.uid,
            email: currentUser.email
          } : null,
          token: localStorage.getItem('authToken') ? 'Presente' : 'Ausente'
        });
      }
      
      toast.error(errorMessage);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setIsValid(null);
    onCouponRemoved();
    toast.success('Cupón removido');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isValid && !appliedCoupon) {
        applyCoupon();
      } else if (!isValid) {
        validateCoupon();
      }
    }
  };

  if (appliedCoupon) {
    return (
      <div className="coupon-field applied">
        <div className="coupon-applied-info">
          <div className="coupon-code">
            <Check size={16} className="check-icon" />
            <span>{appliedCoupon.code}</span>
          </div>
          <div className="coupon-discount">
            <span className="discount-amount">
              -${appliedCoupon.discountAmount.toLocaleString()}
            </span>
            <span className="discount-description">
              ({appliedCoupon.discountType === 'percentage' ? appliedCoupon.discountValue + '%' : '$' + appliedCoupon.discountValue} descuento)
            </span>
          </div>
        </div>
        <button 
          type="button" 
          className="remove-coupon-btn"
          onClick={removeCoupon}
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="coupon-field">
      <div className="coupon-input-group">
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          placeholder="Código de cupón"
          className={`coupon-input ${isValid === true ? 'valid' : isValid === false ? 'invalid' : ''}`}
          disabled={isValidating}
        />
        <div className="coupon-actions">
          {isValidating ? (
            <div className="loading-spinner">
              <Loader size={16} className="spinning" />
            </div>
          ) : isValid === true ? (
            <button
              type="button"
              className="apply-coupon-btn"
              onClick={applyCoupon}
            >
              Aplicar
            </button>
          ) : (
            <button
              type="button"
              className="validate-coupon-btn"
              onClick={validateCoupon}
              disabled={!couponCode.trim()}
            >
              Validar
            </button>
          )}
        </div>
      </div>
      
      {isValid === false && (
        <div className="coupon-error">
          <X size={14} />
          <span>Cupón no válido</span>
        </div>
      )}
      
      {isValid === true && !appliedCoupon && (
        <div className="coupon-success">
          <Check size={14} />
          <span>¡Cupón válido! Aplicando...</span>
        </div>
      )}
    </div>
  );
}
